/**
 * Files, as the patient app uploads them (MASTER_PLAN M8.3/M8.7, API.md §2.3 and §2.4,
 * FR-INS-02, FR-INTAKE-05, SECURITY.md §12.2/§12.3, rules §28).
 *
 * Two surfaces, one shape. The funnel's routes are owned by the `ha_draft` cookie and the
 * portal's by the patient session; the method list is identical apart from the two the
 * public surface does not have (`list` and `remove`), so one factory serves both and the
 * call sites differ by a base path and nothing else.
 *
 * | Method | Funnel | Portal |
 * |---|---|---|
 * | `requestUpload` | `POST /public/files/upload-url` | `POST /patient/files/upload-url` |
 * | `putBytes` | `PUT <uploadUrl>` — storage, not the API | the same |
 * | `complete` | `POST /public/files/:id/complete` | `POST /patient/files/:id/complete` |
 * | `downloadUrl` | `GET /public/files/:id/download-url` | `GET /patient/files/:id/download-url` |
 * | `list` | — | `GET /patient/files` |
 * | `remove` | — | `DELETE /patient/files/:id` |
 *
 * ## Three calls, and none of them may be skipped
 *
 * `requestUpload` → `putBytes` → `complete`. The ticket is a bearer credential with a
 * five-minute life and is the whole authorization for one PUT of one object, so it is
 * never logged, never stored and never put in a query the router can see. Nothing is
 * readable until `complete` has sniffed the first bytes and the scanner has passed it:
 * `FileSummary.downloadable` is the one flag a UI may believe (rules §28.6).
 *
 * ## The pre-check is manners; the API is the rule
 *
 * `FILE_KIND_RULES` is exported by `@platform/contracts` so the builder, the renderer and
 * the API cannot disagree about what a patient may attach. Checking it here turns a 10 MB
 * upload that was going to be refused into a sentence before a byte is spent — and the
 * API checks the size again against the stored object, and the type again against the
 * first bytes, because a client-side check is a courtesy and never an enforcement point
 * (rules §17.2, §28.1).
 *
 * ## Why the PUT does not go through `@platform/api-client`
 *
 * It is not a call to the platform: it is raw bytes to a storage URL, with the headers
 * the ticket named and no cookie at all. It also wants progress, which `fetch` cannot
 * report. `XMLHttpRequest` can, so that is what is used, with a `fetch` fallback for any
 * runtime that has no XHR.
 */

import { isApiError, type ApiClient } from '@platform/api-client';
import {
  ALLOWED_CONTENT_TYPES,
  FILE_KIND_RULES,
  isContentTypeAllowedFor,
  type AllowedContentType,
  type DownloadTicket,
  type FileKind,
  type FileSummary,
  type FileRef,
  type UploadTicket,
} from '@platform/contracts';

import { apiClient } from './client';

/** What the patient app may ask either surface for. Branding kinds are refused there. */
export type PatientFileKind = 'insurance_card_front' | 'insurance_card_back' | 'attachment';

export interface UploadProgress {
  /** Bytes sent so far. */
  loaded: number;
  /** The file's size. Always the same number `requestUpload` was told. */
  total: number;
  /** `loaded / total`, clamped to 0…1. The one number a progress bar needs. */
  ratio: number;
}

export interface UploadOptions {
  /** Called as the bytes leave the browser. Never called after the promise settles. */
  onProgress?: (progress: UploadProgress) => void;
  signal?: AbortSignal;
  /** Sent encrypted and shown back to the patient. Defaults to the browser's own. */
  fileName?: string;
}

export interface FilesClient {
  /** A signed PUT for one object. The ticket is spent by `putBytes` and then discarded. */
  requestUpload(input: {
    kind: PatientFileKind;
    contentType: AllowedContentType;
    sizeBytes: number;
    fileName?: string;
  }): Promise<UploadTicket>;

  /** The bytes, with the ticket's headers verbatim. Resolves once storage has them. */
  putBytes(ticket: UploadTicket, file: Blob, options?: UploadOptions): Promise<void>;

  /** Confirm the upload. The type and size are re-checked; the scan is queued. */
  complete(fileId: string): Promise<FileSummary>;

  /**
   * A short-lived GET.
   *
   * `409 CONFLICT` while the scanner has not answered yet — which is a wait, not a
   * failure (`waitForDownload` below turns it into one).
   */
  downloadUrl(fileId: string): Promise<DownloadTicket>;

  /** Every object on this patient's record. `null` on the public surface — no such route. */
  list: (() => Promise<FileSummary[]>) | null;

  /** Soft-delete. `null` on the public surface: the funnel has no delete route (M8.x). */
  remove: ((fileId: string) => Promise<void>) | null;
}

/* ------------------------------------------------------------------ *
 * The two clients
 * ------------------------------------------------------------------ */

type PutBytes = (ticket: UploadTicket, file: Blob, options: UploadOptions) => Promise<void>;

export interface FilesClientOptions {
  /** Test seam: the raw PUT. Defaults to the XHR uploader below. */
  put?: PutBytes;
}

function createFilesClient(
  client: ApiClient,
  base: string,
  extras: Pick<FilesClient, 'list' | 'remove'>,
  options: FilesClientOptions = {},
): FilesClient {
  const put = options.put ?? putWithProgress;

  return {
    requestUpload: async (input) => {
      const body = await client.request<{ data: UploadTicket }>(
        'post',
        `${base}/upload-url`,
        input,
      );
      return body.data;
    },

    putBytes: (ticket, file, uploadOptions = {}) => put(ticket, file, uploadOptions),

    complete: async (fileId) => {
      const body = await client.request<{ data: { file: FileSummary } }>(
        'post',
        `${base}/${encodeURIComponent(fileId)}/complete`,
        {},
      );
      return body.data.file;
    },

    downloadUrl: async (fileId) => {
      const body = await client.request<{ data: DownloadTicket }>(
        'get',
        `${base}/${encodeURIComponent(fileId)}/download-url`,
      );
      return body.data;
    },

    ...extras,
  };
}

/** The funnel's surface: owned by the `ha_draft` cookie, no list and no delete. */
export function createPublicFilesClient(
  client: ApiClient,
  options: FilesClientOptions = {},
): FilesClient {
  return createFilesClient(client, '/api/v1/public/files', { list: null, remove: null }, options);
}

/** The signed-in patient's own surface. */
export function createPatientFilesClient(
  client: ApiClient,
  options: FilesClientOptions = {},
): FilesClient {
  const base = '/api/v1/patient/files';
  return createFilesClient(
    client,
    base,
    {
      list: async () => {
        const body = await client.request<{ data: FileSummary[] }>('get', base);
        return body.data;
      },
      remove: async (fileId) => {
        await client.request<void>('delete', `${base}/${encodeURIComponent(fileId)}`);
      },
    },
    options,
  );
}

export const publicFilesClient: FilesClient = createPublicFilesClient(apiClient);
export const patientFilesClient: FilesClient = createPatientFilesClient(apiClient);

/* ------------------------------------------------------------------ *
 * The raw PUT
 * ------------------------------------------------------------------ */

/**
 * The bytes, with progress.
 *
 * No credentials: the signed URL *is* the authorization, and sending the session cookie
 * to a storage host would hand a third party a credential it has no use for
 * (SECURITY.md §12.2). The ticket's `headers` go up exactly as they arrived — they are
 * part of what the signature covers, so an added or a dropped one is a `403` from
 * storage rather than a helpful correction.
 */
function putWithProgress(ticket: UploadTicket, file: Blob, options: UploadOptions): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    if (typeof XMLHttpRequest === 'undefined') {
      void fetch(ticket.uploadUrl, { method: 'PUT', headers: ticket.headers, body: file })
        .then((response) => {
          if (response.ok) resolve();
          else reject(new FileUploadError(`The upload was refused (${String(response.status)}).`));
        })
        .catch(() => {
          reject(new FileUploadError(UPLOAD_MESSAGES.network));
        });
      return;
    }

    const request = new XMLHttpRequest();
    request.open('PUT', ticket.uploadUrl, true);
    for (const [name, value] of Object.entries(ticket.headers)) {
      request.setRequestHeader(name, value);
    }

    const total = file.size;
    request.upload.onprogress = (event) => {
      const loaded = event.lengthComputable ? event.loaded : 0;
      options.onProgress?.({ loaded, total, ratio: total === 0 ? 0 : Math.min(loaded / total, 1) });
    };

    request.onload = () => {
      if (request.status >= 200 && request.status < 300) {
        options.onProgress?.({ loaded: total, total, ratio: 1 });
        resolve();
        return;
      }
      reject(new FileUploadError(`The upload was refused (${String(request.status)}).`));
    };
    request.onerror = () => {
      reject(new FileUploadError(UPLOAD_MESSAGES.network));
    };
    request.onabort = () => {
      reject(new DOMException('The upload was cancelled.', 'AbortError'));
    };

    if (options.signal !== undefined) {
      if (options.signal.aborted) {
        request.abort();
        return;
      }
      options.signal.addEventListener('abort', () => {
        request.abort();
      });
    }

    request.send(file);
  });
}

/* ------------------------------------------------------------------ *
 * The pre-check
 * ------------------------------------------------------------------ */

/** The copy, in one place so a sentence is changed once (rules §35.2). */
export const UPLOAD_MESSAGES = {
  tooLarge: (mb: number) =>
    `That file is larger than ${String(mb)} MB. Please choose a smaller one.`,
  wrongType: 'Please choose a JPEG, PNG, HEIC or PDF.',
  empty: 'That file is empty. Please choose another one.',
  network: 'We could not upload that file. Please check your connection and try again.',
  scanning: 'We are checking that file. This usually takes a few seconds.',
  failed: 'We could not upload that file. Please try again.',
} as const;

/** A failure with a sentence the patient can act on. Never a storage or vendor string. */
export class FileUploadError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'FileUploadError';
  }
}

/**
 * What the browser thinks this file is, as one of the five accepted types.
 *
 * A HEIC photographed on an iPhone often arrives with an empty `type`, because the
 * platform's own picker does not always set one — so the extension is read as a last
 * resort. It is a *hint* either way: the API sniffs the stored bytes and rejects a file
 * that is not what it claimed (SECURITY.md §12.3).
 */
export function contentTypeOf(file: { type: string; name: string }): AllowedContentType | null {
  const declared = file.type.toLowerCase();
  if ((ALLOWED_CONTENT_TYPES as readonly string[]).includes(declared)) {
    return declared as AllowedContentType;
  }
  if (declared !== '') return null;

  const extension = file.name.toLowerCase().split('.').pop() ?? '';
  const byExtension: Record<string, AllowedContentType> = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    heic: 'image/heic',
    heif: 'image/heif',
    pdf: 'application/pdf',
  };
  return byExtension[extension] ?? null;
}

/** `null` when the file may be sent, or the sentence explaining why it may not. */
export function precheckFile(kind: PatientFileKind, file: File): string | null {
  if (file.size === 0) return UPLOAD_MESSAGES.empty;

  const contentType = contentTypeOf(file);
  if (contentType === null || !isContentTypeAllowedFor(kind, contentType)) {
    return UPLOAD_MESSAGES.wrongType;
  }

  const { maxSizeBytes } = FILE_KIND_RULES[kind];
  if (file.size > maxSizeBytes) {
    return UPLOAD_MESSAGES.tooLarge(Math.floor(maxSizeBytes / (1024 * 1024)));
  }
  return null;
}

/* ------------------------------------------------------------------ *
 * The three calls as one
 * ------------------------------------------------------------------ */

/**
 * Pre-check, ticket, bytes, completion — the whole upload, as one promise.
 *
 * Refusals keep the API's own sentence (`413` names the ceiling, `415` names the accepted
 * types), because those are written to be shown and re-writing them here would give the
 * patient two vocabularies for one refusal (API.md §1.6, rules §35.2).
 */
export async function uploadFile(
  client: FilesClient,
  kind: PatientFileKind,
  file: File,
  options: UploadOptions = {},
): Promise<FileSummary> {
  const refusal = precheckFile(kind, file);
  if (refusal !== null) throw new FileUploadError(refusal);

  const contentType = contentTypeOf(file);
  /* `precheckFile` already refused a null, so this is a type narrowing and not a check. */
  if (contentType === null) throw new FileUploadError(UPLOAD_MESSAGES.wrongType);

  const ticket = await client.requestUpload({
    kind,
    contentType,
    sizeBytes: file.size,
    fileName: options.fileName ?? file.name,
  });

  await client.putBytes(ticket, file, options);
  return client.complete(ticket.fileId);
}

/**
 * A `FileRef` for an intake `file` field, which is what the renderer's `UploadAdapter`
 * wants (INTAKE_FORMS.md §7.3).
 *
 * The answer stores an id, a name and a size — never a URL: a link with a five-minute
 * life stored in a submission is a dead link by the time staff read it, and the object is
 * fetched through the authorization path every time instead.
 */
export function createUploadAdapter(
  client: FilesClient,
  kind: PatientFileKind = 'attachment',
): { requestUpload(file: File): Promise<FileRef> } {
  return {
    requestUpload: async (file) => {
      const summary = await uploadFile(client, kind, file);
      return {
        fileId: summary.id,
        name: summary.name ?? file.name,
        contentType: summary.contentType,
        sizeBytes: summary.sizeBytes,
      };
    },
  };
}

/**
 * Wait for the scanner, then hand back a download ticket.
 *
 * `409 CONFLICT` means "still being checked", which is a spinner and not a failure
 * (rules §28.6): a clean file that arrives a second later would otherwise be reported to
 * the patient as a broken upload. Anything else — `404`, a rejected object, the network —
 * is thrown, because those do not get better by waiting.
 */
export async function waitForDownload(
  client: FilesClient,
  fileId: string,
  options: { attempts?: number; delayMs?: number; signal?: AbortSignal } = {},
): Promise<DownloadTicket> {
  const attempts = options.attempts ?? 6;
  const delayMs = options.delayMs ?? 1_000;

  for (let attempt = 0; attempt < attempts; attempt += 1) {
    if (options.signal?.aborted === true) {
      throw new DOMException('The wait was cancelled.', 'AbortError');
    }
    try {
      return await client.downloadUrl(fileId);
    } catch (error) {
      if (!isScanPending(error) || attempt === attempts - 1) throw error;
      await delay(delayMs);
    }
  }
  /* Unreachable: the loop either returns or throws on its last attempt. */
  throw new FileUploadError(UPLOAD_MESSAGES.scanning);
}

/** `409 CONFLICT` from the download route — the scanner has not answered yet. */
export function isScanPending(error: unknown): boolean {
  return isApiError(error) && error.code === 'CONFLICT';
}

/** A sentence for the patient, whatever went wrong. */
export function describeUploadError(error: unknown): string {
  if (error instanceof FileUploadError) return error.message;
  if (isApiError(error)) return error.message;
  return UPLOAD_MESSAGES.failed;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/** Re-exported so a screen can name a ceiling without importing the contract twice. */
export { FILE_KIND_RULES };
export type { FileSummary, UploadTicket, DownloadTicket, FileKind };
